import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as CoachesIcon,
  Group as PlayersIcon,
  AdminPanelSettings as AdminIcon,
  EmojiEvents as CertificatesIcon,
  TrendingUp as RankingIcon,
} from '@mui/icons-material';

interface AcademyDashboardProps {
  academyId: string;
  academyInfo: any;
}

const AcademyDashboard: React.FC<AcademyDashboardProps> = ({ academyId, academyInfo }) => {
  const [stats, setStats] = useState({
    coaches: 0,
    players: 0,
    admins: 0,
    certificates: 0,
    rankings: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, [academyId]);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all stats in parallel
      const [coachesRes, playersRes, adminsRes] = await Promise.all([
        fetch(`/api/academies/${academyId}/coaches`),
        fetch(`/api/academies/${academyId}/players`),
        fetch(`/api/academies/${academyId}/admins`),
      ]);

      const coachesData = await coachesRes.json();
      const playersData = await playersRes.json();
      const adminsData = await adminsRes.json();

      setStats({
        coaches: coachesData.success ? (coachesData.data?.length || 0) : 0,
        players: playersData.success ? (playersData.data?.length || 0) : 0,
        admins: adminsData.success ? (adminsData.data?.length || 0) : 0,
        certificates: 0, // TODO: Implement certificates count
        rankings: 0, // TODO: Implement rankings count
      });
    } catch (err: any) {
      console.error('Error loading stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    {
      title: 'Coaches',
      value: stats.coaches,
      icon: <CoachesIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      color: '#1976d2',
    },
    {
      title: 'Players/Students',
      value: stats.players,
      icon: <PlayersIcon sx={{ fontSize: 40, color: '#2e7d32' }} />,
      color: '#2e7d32',
    },
    {
      title: 'Admins',
      value: stats.admins,
      icon: <AdminIcon sx={{ fontSize: 40, color: '#ed6c02' }} />,
      color: '#ed6c02',
    },
    {
      title: 'Certificates',
      value: stats.certificates,
      icon: <CertificatesIcon sx={{ fontSize: 40, color: '#9c27b0' }} />,
      color: '#9c27b0',
    },
    {
      title: 'Rankings',
      value: stats.rankings,
      icon: <RankingIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
      color: '#d32f2f',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
        Welcome to your academy management dashboard
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 80, height: 80, borderRadius: 2, bgcolor: `${card.color}15` }}>
                  {card.icon}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 600, color: card.color }}>
                    {card.value}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {card.title}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {academyInfo && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Academy Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {academyInfo.academy_id_code && (
              <Typography variant="body2">
                <strong>Academy ID:</strong> <span style={{ fontFamily: 'monospace', color: '#1976d2', fontWeight: 600 }}>{academyInfo.academy_id_code}</span>
              </Typography>
            )}
            <Typography variant="body2">
              <strong>Name:</strong> {academyInfo.name}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {academyInfo.academy_type === 'regular_school' ? 'Regular School' : 'Sports Academy'}
            </Typography>
            {academyInfo.city && (
              <Typography variant="body2">
                <strong>Location:</strong> {academyInfo.city}{academyInfo.country ? `, ${academyInfo.country}` : ''}
              </Typography>
            )}
            {academyInfo.contact_email && (
              <Typography variant="body2">
                <strong>Contact Email:</strong> {academyInfo.contact_email}
              </Typography>
            )}
            {academyInfo.contact_phone && (
              <Typography variant="body2">
                <strong>Contact Phone:</strong> {academyInfo.contact_phone}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AcademyDashboard;

